import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { objectsApi } from './api'
import type { ObjectDocument, ObjectVideo } from './api'
import type { Object as ObjectEntity, RunbookTask } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'
import { keepPreviousData } from '@tanstack/react-query'
import { debounce } from '@/lib/utils'

// Type helper for backend responses that may be wrapped in { success: true, data: T }
type BackendResponse<T> = T | { success: boolean; data: T }

export function useObjects(params?: ListParams) {
    return useQuery({
        queryKey: ['objects', params],
        queryFn: async () => {
            const response = await objectsApi.list(params)
            // Backend returns { success: true, data: [...], page, perPage, total, ... }
            // Axios wraps it, so response.data contains the backend response
            return response.data
        },
        placeholderData: keepPreviousData,
        // RTO/status are computed live (ObjectRTO view) and change when a runbook is
        // edited on another screen. staleTime 0 + refetchOnMount 'always' means the
        // list always refetches on navigation AND on search/filter changes, so it
        // never serves the stale 5-minute global cache (which showed old 0m RTOs).
        staleTime: 0,
        refetchOnMount: 'always',
    })
}

export function useObject(id: string | null, scenario?: string) {
    return useQuery({
        queryKey: ['objects', id, scenario],
        queryFn: async () => {
            if (!id) return null
            // Decode the ID if it was URL encoded
            const decodedId = decodeURIComponent(id)
            const response = await objectsApi.get(decodedId, scenario)
            // Backend returns { success: true, data: {...}, ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<ObjectEntity>
            return (backendData as { data?: ObjectEntity })?.data || backendData as ObjectEntity
        },
        enabled: !!id,
    })
}

export function useCreateObject() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: Omit<ObjectEntity, 'objectID'>) => objectsApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objects'] })
        },
    })
}

export function useUpdateObject() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<Omit<ObjectEntity, 'objectID'>> }) =>
            objectsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objects'] })
        },
    })
}

export function useDeleteObject() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => objectsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objects'] })
        },
    })
}

export function useObjectVersions(objectID: string | null) {
    return useQuery({
        queryKey: ['objects', objectID, 'versions'],
        queryFn: async () => {
            if (!objectID) return []
            // Decode the ID if it was URL encoded
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getVersions(decodedId)
            // Backend returns { success: true, data: [...], ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<import('@/types/entities').ObjectVersion[]>
            return (backendData as { data?: import('@/types/entities').ObjectVersion[] })?.data || backendData as import('@/types/entities').ObjectVersion[] || []
        },
        enabled: !!objectID,
    })
}

export function useObjectRtoDetails(objectID: string | null, enabled: boolean, scenario?: string) {
    return useQuery({
        queryKey: ['objects', objectID, 'rto-details', scenario],
        queryFn: async () => {
            if (!objectID) return null
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getRtoDetails(decodedId, scenario)
            const backendData = response.data as BackendResponse<{ egressTime: number | null; linkTime: number | null; engineerRTO: number | null; endToEndTime: number | null }>
            return (backendData as { data?: typeof backendData })?.data || backendData
        },
        enabled: !!objectID && enabled,
    })
}

export function useRolledUpRTO(objectID: string | null, scenario?: string) {
    return useQuery({
        queryKey: ['objects', objectID, 'rolled-up-rto', scenario],
        queryFn: async () => {
            if (!objectID) return []
            // Decode the ID if it was URL encoded
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getRolledUpRTO(decodedId, scenario)
            // Backend returns { success: true, data: [...], ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<Array<{ objectID: string; objectDescription: string; layerID: number | null; rto: number | null }>>
            return (backendData as { data?: typeof backendData })?.data || backendData || []
        },
        enabled: !!objectID,
    })
}

export function useRolledUpRPO(objectID: string | null, scenario?: string) {
    return useQuery({
        queryKey: ['objects', objectID, 'rolled-up-rpo', scenario],
        queryFn: async () => {
            if (!objectID) return []
            // Decode the ID if it was URL encoded
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getRolledUpRPO(decodedId, scenario)
            // Backend returns { success: true, data: [...], ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<Array<{ objectID: string; objectDescription: string; sourceLayer: number | null; rpo: number | null }>>
            return (backendData as { data?: typeof backendData })?.data || backendData || []
        },
        enabled: !!objectID,
    })
}

export function useServiceTable(objectID: string | null, scenario?: string) {
    return useQuery({
        queryKey: ['objects', objectID, 'service-table', scenario],
        queryFn: async () => {
            if (!objectID) return []
            // Decode the ID if it was URL encoded
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getServiceTable(decodedId, scenario)
            // Backend returns { success: true, data: [...], ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<Array<{
                sourceID: string;
                description: string;
                sourceLayer: number | null;
                calculatedRPO: number | null;
                endToEndTime: number | null;
                metadata: string | null;
                vaultFiles: string | null;
                objectStatus: number | null;
                objectSize: number | null;
            }>>
            return (backendData as { data?: typeof backendData })?.data || backendData || []
        },
        enabled: !!objectID,
    })
}

export function useRunbook(objectID: string | null, scenario?: string) {
    return useQuery({
        queryKey: ['objects', objectID, 'runbook', scenario],
        queryFn: async () => {
            if (!objectID) return []
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getRunbook(decodedId, scenario)
            const backendData = response.data as BackendResponse<RunbookTask[]>
            return (backendData as { data?: RunbookTask[] })?.data || backendData || []
        },
        enabled: !!objectID,
    })
}

export function useSaveRunbook() {
    const queryClient = useQueryClient()

    // Create a debounced refetch function to avoid triggering threat detection
    // This will refetch in the background after a delay to ensure data consistency
    const debouncedRefetch = useMemo(
        () => debounce((queryKey: (string | number | undefined)[]) => {
            queryClient.invalidateQueries({
                queryKey,
                refetchType: 'active' // Only refetch if query is currently active/observed
            })
        }, 10000), // 10 second debounce to avoid rapid requests
        [queryClient]
    )

    return useMutation({
        mutationFn: ({ objectID, tasks, scenario }: { objectID: string; tasks: RunbookTask[]; scenario?: string }) =>
            objectsApi.saveRunbook(objectID, tasks, scenario),

        // Optimistic update: Update UI immediately for better UX
        onMutate: async (variables) => {
            // Cancel any outgoing refetches to avoid overwriting optimistic update
            await queryClient.cancelQueries({
                queryKey: ['objects', variables.objectID, 'runbook', variables.scenario]
            })

            // Snapshot the previous value for potential rollback
            const previousTasks = queryClient.getQueryData<RunbookTask[]>([
                'objects',
                variables.objectID,
                'runbook',
                variables.scenario
            ])

            // Optimistically update the cache with new data
            queryClient.setQueryData(
                ['objects', variables.objectID, 'runbook', variables.scenario],
                variables.tasks
            )

            // Return context with previous value for rollback on error
            return { previousTasks }
        },

        // On error: Rollback to previous state
        onError: (error, variables, context) => {
            // Rollback optimistic update if we have previous data
            if (context?.previousTasks) {
                queryClient.setQueryData(
                    ['objects', variables.objectID, 'runbook', variables.scenario],
                    context.previousTasks
                )
            }

            // Log error for monitoring (in production, send to error tracking service)
            console.error('[useSaveRunbook] Save failed, rolled back optimistic update', {
                error,
                objectID: variables.objectID,
                scenario: variables.scenario,
                taskCount: variables.tasks.length
            })
        },

        // On success: Update cache and schedule background sync
        onSuccess: (response, variables) => {
            // Update cache with saved data (in case server transformed it)
            // The response should contain the saved tasks with any server-side modifications
            queryClient.setQueryData(
                ['objects', variables.objectID, 'runbook', variables.scenario],
                variables.tasks // Use the tasks we sent (server should return same or transformed)
            )

            // The RTO (ObjectRTO view) recomputes from the saved runbook tasks, so refresh
            // the top-card RTO/RPO queries immediately so End to End / Rolled Up RTO update.
            const s = variables.scenario
            const oid = variables.objectID
            queryClient.invalidateQueries({ queryKey: ['objects', oid, 'rto-details', s] })
            queryClient.invalidateQueries({ queryKey: ['objects', oid, 'rolled-up-rto', s] })
            queryClient.invalidateQueries({ queryKey: ['objects', oid, 'rolled-up-rpo', s] })
            queryClient.invalidateQueries({ queryKey: ['objects', oid, s] })

            // Schedule debounced background refetch to ensure consistency
            // This happens after a delay to avoid triggering threat detection
            debouncedRefetch(['objects', variables.objectID, 'runbook', variables.scenario])
        },

        // On settle: Always ensure eventual consistency
        onSettled: (data, error, variables) => {
            // If there was an error, don't schedule refetch (already rolled back)
            if (error) return

            // Schedule background sync after a longer delay for eventual consistency
            // This ensures we pick up any changes from other users or server-side modifications
            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: ['objects', variables.objectID, 'runbook', variables.scenario],
                    refetchType: 'active' // Only refetch if query is active
                })
            }, 30000) // 30 second delay for background sync
        }
    })
}

export function useScenarios(objectID: string | null) {
    return useQuery({
        queryKey: ['objects', objectID, 'scenarios'],
        queryFn: async () => {
            if (!objectID) return []
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getScenarios(decodedId)
            const backendData = response.data as BackendResponse<Array<{ id: number; name: string; isDefault: boolean }>>
            return (backendData as { data?: typeof backendData })?.data || backendData || []
        },
        enabled: !!objectID,
    })
}

export function useScenario(objectID: string | null, scenarioId: number | null) {
    return useQuery({
        queryKey: ['objects', objectID, 'scenarios', scenarioId],
        queryFn: async () => {
            if (!objectID || !scenarioId) return null
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getScenario(decodedId, scenarioId)
            const backendData = response.data as BackendResponse<{ id: number; name: string; description: string; useCase: string }>
            return (backendData as { data?: typeof backendData })?.data || backendData
        },
        enabled: !!objectID && !!scenarioId,
    })
}

export function useObjectsStatsCount() {
    return useQuery({
        queryKey: ['objects', 'stats', 'count'],
        queryFn: async () => {
            const response = await objectsApi.getStatsCount()
            // Backend returns { success: true, data: {...}, ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<{ objects: number; services: number }>
            return (backendData as { data?: typeof backendData })?.data || backendData
        },
    })
}

export function useObjectsInformation(objectID: string | null, scenario?: string) {
    return useQuery({
        queryKey: ['objects', objectID, 'information', scenario],
        queryFn: async () => {
            if (!objectID || !scenario) return null
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getObjectsInformation(decodedId, scenario)
            // Backend returns { success: true, data: {...}, ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<{ id: number | null; scenario: number | null; objectID: string | null; floorPlan: number | null; documentID: number | null; videoID: number | null; image: string | null }>
            return (backendData as { data?: typeof backendData })?.data || backendData || null
        },
        enabled: !!objectID && !!scenario,
    })
}

export function useObjectsStatsByLocation() {
    return useQuery({
        queryKey: ['objects', 'stats', 'by-location'],
        queryFn: async () => {
            const response = await objectsApi.getStatsByLocation()
            // Backend returns { success: true, data: {...}, ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<{ objects: Array<{ Location: string; NoObjects: number }>; services: Array<{ Location: string; NoObjects: number }> }>
            return (backendData as { data?: typeof backendData })?.data || backendData
        },
    })
}

export function useRetrieveVersion() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: { objectID: string; versionID: number | null; recoverRedObjects: string }) => {
            return objectsApi.retrieveVersion(data).then((response) => {
                const backendData = response.data as BackendResponse<{ success: boolean; sqlState?: string; sqlMessage?: string; outcome?: string }>
                return (backendData as { data?: typeof backendData })?.data || backendData
            })
        },
        onSuccess: () => {
            // Invalidate all object-related queries
            queryClient.invalidateQueries({ queryKey: ['objects'] })
            // Invalidate jobs/recovery requests queries to refresh the recovery request table
            queryClient.invalidateQueries({ queryKey: ['jobs'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-requests'] })
            queryClient.invalidateQueries({ queryKey: ['recover-requests'] })
        },
    })
}

export function useStackRecoveryStats(objectID: string | null, minSourceLayer?: number) {
    return useQuery({
        queryKey: ['objects', objectID, 'stack-recovery-stats', minSourceLayer],
        queryFn: async () => {
            if (!objectID) return null
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getStackRecoveryStats(decodedId, minSourceLayer)
            // Backend returns { success: true, data: {...}, ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<{ servicesCount: number; objectsCount: number; redObjectsCount: number; minSourceLayer: number; maxLayerID: number }>
            // Extract data from response structure
            if (backendData && typeof backendData === 'object' && 'data' in backendData) {
                const wrapped = backendData as { data?: { servicesCount: number; objectsCount: number; redObjectsCount: number; minSourceLayer: number; maxLayerID: number } }
                return wrapped.data || null
            }
            // Otherwise, backendData is the object directly
            return backendData as { servicesCount: number; objectsCount: number; redObjectsCount: number; minSourceLayer: number; maxLayerID: number } || null
        },
        enabled: !!objectID,
    })
}

export function useRedObjects(objectID: string | null) {
    return useQuery({
        queryKey: ['objects', objectID, 'red-objects'],
        queryFn: async () => {
            if (!objectID) return []
            const decodedId = decodeURIComponent(objectID)
            const response = await objectsApi.getRedObjects(decodedId)
            // Backend returns { success: true, data: [...], ... }
            // Axios wraps it, so response.data contains the backend response
            const backendData = response.data as BackendResponse<Array<{
                redId: number | null;
                objectID: string;
                objectSecondary: string;
                redObjectName: string;
                redAnalytics: number | null;
                redDateTime: string | null;
                redSize: number | null;
                greenId: number | null;
                greenObjectName: string;
                greenAnalytics: number | null;
                greenDateTime: string | null;
                greenSize: number | null;
            }>>
            // Extract data from response structure
            if (backendData && typeof backendData === 'object' && 'data' in backendData) {
                const wrapped = backendData as { data?: any }
                return Array.isArray(wrapped.data) ? wrapped.data : []
            }
            // Otherwise, backendData is the array directly
            return Array.isArray(backendData) ? backendData : []
        },
        enabled: !!objectID,
    })
}

export function useRequestStackRecovery() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: { objectID: string; recoverRedObjects: string; minSourceLayer: number; scenario: number }) => {
            return objectsApi.requestStackRecovery(data).then((response) => {
                const backendData = response.data as BackendResponse<{ success: boolean; sqlState?: string; sqlMessage?: string; outcome?: string }>
                return (backendData as { data?: typeof backendData })?.data || backendData
            })
        },
        onSuccess: () => {
            // Invalidate all object-related queries
            queryClient.invalidateQueries({ queryKey: ['objects'] })
            // Invalidate jobs/recovery requests queries to refresh the recovery request table
            queryClient.invalidateQueries({ queryKey: ['jobs'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-requests'] })
            queryClient.invalidateQueries({ queryKey: ['recover-requests'] })
        },
    })
}

// ==========================================
// Document & Video hooks
// ==========================================

type BackendArrayResponse<T> = T[] | { success: boolean; data: T[] }

export function useObjectDocuments(objectID: string | null, scenario?: string) {
    return useQuery({
        queryKey: ['objects', objectID, 'documents', scenario],
        queryFn: async () => {
            if (!objectID || !scenario) return []
            const response = await objectsApi.getDocuments(decodeURIComponent(objectID), scenario)
            const bd = response.data as BackendArrayResponse<ObjectDocument>
            return (bd as { data?: ObjectDocument[] })?.data || bd as ObjectDocument[] || []
        },
        enabled: !!objectID && !!scenario,
    })
}

export function useObjectVideos(objectID: string | null, scenario?: string) {
    return useQuery({
        queryKey: ['objects', objectID, 'videos', scenario],
        queryFn: async () => {
            if (!objectID || !scenario) return []
            const response = await objectsApi.getVideos(decodeURIComponent(objectID), scenario)
            const bd = response.data as BackendArrayResponse<ObjectVideo>
            return (bd as { data?: ObjectVideo[] })?.data || bd as ObjectVideo[] || []
        },
        enabled: !!objectID && !!scenario,
    })
}

export function useDocumentLibrary() {
    return useQuery({
        queryKey: ['objects', 'documents', 'library'],
        queryFn: async () => {
            const response = await objectsApi.getDocumentLibrary()
            const bd = response.data as BackendArrayResponse<ObjectDocument>
            return (bd as { data?: ObjectDocument[] })?.data || bd as ObjectDocument[] || []
        },
        enabled: false,
    })
}

export function useVideoLibrary() {
    return useQuery({
        queryKey: ['objects', 'videos', 'library'],
        queryFn: async () => {
            const response = await objectsApi.getVideoLibrary()
            const bd = response.data as BackendArrayResponse<ObjectVideo>
            return (bd as { data?: ObjectVideo[] })?.data || bd as ObjectVideo[] || []
        },
        enabled: false,
    })
}

export function useAddDocument() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ objectID, data }: { objectID: string; data: { scenario: number; filename: string; originalName: string; description?: string; fileSize?: number; mimeType?: string } }) =>
            objectsApi.addDocument(objectID, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objects'] })
        },
    })
}

export function useDeleteDocument() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ objectID, docId }: { objectID: string; docId: number }) =>
            objectsApi.deleteDocument(objectID, docId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objects'] })
        },
    })
}

export function useAddVideo() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ objectID, data }: { objectID: string; data: { scenario: number; filename: string; originalName: string; description?: string; fileSize?: number; mimeType?: string; duration?: number } }) =>
            objectsApi.addVideo(objectID, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objects'] })
        },
    })
}

export function useDeleteVideo() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ objectID, videoId }: { objectID: string; videoId: number }) =>
            objectsApi.deleteVideo(objectID, videoId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['objects'] })
        },
    })
}
